import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Popover,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";

import DateRangeFields from "./DateRangeFields";
import { dateFilterControlSx } from "./AppDatePicker";
import { formatAppRangeLabelDate, getAppNow } from "../../utils/appTime";

export const DEFAULT_DATE_PRESET_OPTIONS = [
    { value: "all", label: "All Dates" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7", label: "Last 7 Days" },
    { value: "last30", label: "Last 30 Days" },
    { value: "thisMonth", label: "This Month" },
    { value: "lastMonth", label: "Last Month" },
    { value: "custom", label: "Custom Range" },
];

export const getPresetDateRange = (preset) => {
    const now = getAppNow();

    switch (preset) {
        case "today":
            return [now.startOf("day"), now.endOf("day")];
        case "yesterday": {
            const yesterday = now.subtract(1, "day");
            return [yesterday.startOf("day"), yesterday.endOf("day")];
        }
        case "last7":
            return [now.subtract(6, "day").startOf("day"), now.endOf("day")];
        case "last30":
            return [now.subtract(29, "day").startOf("day"), now.endOf("day")];
        case "thisMonth":
            return [now.startOf("month"), now.endOf("month")];
        case "lastMonth": {
            const lastMonth = now.subtract(1, "month");
            return [lastMonth.startOf("month"), lastMonth.endOf("month")];
        }
        default:
            return [null, null];
    }
};

const formatRangeLabel = (range = []) => {
    const [start, end] = range;
    if (!start || !end) return "Custom Range";

    return `${formatAppRangeLabelDate(start)} - ${formatAppRangeLabelDate(end)}`;
};

// Caller state shape:
//   { type: string, customRange: [dayjs|null, dayjs|null], range: [dayjs|null, dayjs|null] }
//   - type:        current preset key ("all", "today", "last7", "custom", …)
//   - customRange: the user's raw date-picker input (only used when type === "custom")
//   - range:       the resolved interval sent to the API (set by getPresetDateRange or by onCustomRangeChange)
// Pass `value={state.type}` and `customRange={state.customRange}`.
// Use onPresetChange to update `type` (and derive `range` via getPresetDateRange).
// Use onCustomRangeChange to update both `customRange` and `range` together.
export default function PresetDateRangeFilter({
    label = "Date",
    value,
    options = DEFAULT_DATE_PRESET_OPTIONS,
    customRange = [null, null],
    onPresetChange,
    onCustomRangeChange,
    onChange,
    customValue = "custom",
    startLabel = "From Date",
    endLabel = "To Date",
    minPresetWidth = 180,
}) {
    const anchorRef = useRef(null);
    const [draftRange, setDraftRange] = useState(customRange);
    const [pickerOpen, setPickerOpen] = useState(false);
    const popupOpen = pickerOpen;

    useEffect(() => {
        if (!pickerOpen) {
            setDraftRange(customRange);
        }
    }, [customRange, pickerOpen]);

    const emitChange = (preset, range) => {
        onChange?.({
            preset,
            startDate: range?.[0] || null,
            endDate: range?.[1] || null,
        });
    };

    const openCustomPicker = () => {
        setDraftRange(customRange);
        window.setTimeout(() => setPickerOpen(true), 0);
    };

    const handlePresetChange = (event) => {
        const nextPreset = event.target.value;

        if (nextPreset === customValue) {
            openCustomPicker();
            return;
        }

        onPresetChange?.(nextPreset);
        const nextRange = getPresetDateRange(nextPreset);
        emitChange(nextPreset, nextRange);
    };

    const handleApplyCustomRange = () => {
        const nextRange = draftRange || [null, null];
        onPresetChange?.(customValue);
        onCustomRangeChange?.(nextRange);
        emitChange(customValue, nextRange);
        setPickerOpen(false);
    };

    const handleCancelCustomRange = () => {
        setDraftRange(customRange);
        setPickerOpen(false);
    };

    const selectedLabel =
        value === customValue
            ? formatRangeLabel(customRange)
            : options.find((option) => option.value === value)?.label || "";

    return (
        <>
            <Box ref={anchorRef} sx={{ width: "100%" }}>
                <FormControl
                    fullWidth
                    size="small"
                    sx={{
                        minWidth: minPresetWidth,
                        flex: `1 1 ${minPresetWidth}px`,
                        ...dateFilterControlSx,
                    }}
                >
                    <InputLabel>{label}</InputLabel>
                    <Select
                        value={value}
                        label={label}
                        onChange={handlePresetChange}
                        renderValue={() => selectedLabel}
                        sx={{
                            minHeight: 40,
                            borderRadius: "10px",
                            "& .MuiSelect-select": {
                                display: "flex",
                                alignItems: "center",
                                minHeight: "auto",
                                py: 1,
                            },
                        }}
                    >
                        {options.map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                onClick={option.value === customValue ? openCustomPicker : undefined}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Popover
                open={popupOpen}
                anchorEl={anchorRef.current}
                onClose={handleCancelCustomRange}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        p: 2,
                        width: { xs: "calc(100% - 32px)", sm: 420 },
                        maxWidth: "calc(100vw - 32px)",
                        borderRadius: 3,
                        border: "1px solid rgba(148,163,184,0.18)",
                        boxShadow: "0 18px 48px rgba(15, 23, 42, 0.18)",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Box>
                        <Typography fontWeight={800}>Custom Range</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Select a start and end date, then apply the range.
                        </Typography>
                    </Box>

                    <DateRangeFields
                        value={draftRange}
                        onChange={(range) => setDraftRange(range || [null, null])}
                        startLabel={startLabel}
                        endLabel={endLabel}
                    />

                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                        <Button onClick={handleCancelCustomRange}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleApplyCustomRange}
                            disabled={!draftRange?.[0] || !draftRange?.[1]}
                        >
                            Apply
                        </Button>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
}
